import React, { useMemo } from "react";
import { createContext, useContext } from "react";
import { useUserDetailQuery } from "../hooks/AccountHooks";
import { VerifiedIdIssuanceModel } from "../types/models";

export interface authContext {
    isAuthenticated: boolean | undefined;
    isAuthError: boolean;
    isLoading: boolean;
    sessionTimeoutMinutes: number;
    sessionTimeoutWarningMinutes: number;
    verifiedIdEnabled: boolean;
    tapEnabled: boolean;
    requiresIdentityReauth: boolean;
    verifiedIdIssuance: VerifiedIdIssuanceModel | null;
    login: () => void;
    logout: () => void;
    altlogin: () => void;
}

export const AuthContext = createContext<authContext>({
    isAuthenticated: false,
    isAuthError: false,
    isLoading: true,
    sessionTimeoutMinutes: 30,
    sessionTimeoutWarningMinutes: 3,
    verifiedIdEnabled: false,
    tapEnabled: false,
    requiresIdentityReauth: false,
    verifiedIdIssuance: null,
    login: function(): void {
        throw new Error("Function not implemented.");
    },
    logout: function(): void {
        throw new Error("Function not implemented.");
    },
    altlogin: function (): void {
        throw new Error("Function not implemented.");
    },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: React.PropsWithChildren<{}>) => 
{
    const { data: userDetail, isPending, isError: queryIsError } = useUserDetailQuery();

    const isAuthenticated = userDetail?.isAuthenticated;
    // Mirrors pre-refactor behavior: authenticated-but-no-profile is treated as an auth error too.
    const isAuthError = queryIsError 
        || (userDetail?.isAuthenticated === true && userDetail?.userProfile === null);
    const sessionTimeoutMinutes = userDetail?.sessionTimeoutMinutes ?? 30;
    const sessionTimeoutWarningMinutes = userDetail?.sessionTimeoutWarningMinutes ?? 3;
    const verifiedIdEnabled = userDetail?.verifiedIdEnabled ?? false;
    const tapEnabled = userDetail?.tapEnabled ?? false;
    const requiresIdentityReauth = userDetail?.requiresIdentityReauth ?? false;
    const verifiedIdIssuance = userDetail?.userProfile?.verifiedIdIssuance ?? null;

    const login = () => {
        window.location.href = '/auth/login';
    }

    const altlogin = () => {
        window.location.href = '/auth/altlogin';
    }

    const logout = () => {
        window.location.href = '/auth/logout';
    }

    const contextValue = useMemo(() => ({
        isAuthenticated,
        isAuthError,
        isLoading: isPending,
        sessionTimeoutMinutes,
        sessionTimeoutWarningMinutes,
        verifiedIdEnabled,
        tapEnabled,
        requiresIdentityReauth,
        verifiedIdIssuance,
        login,
        logout,
        altlogin,
    }), [
        isAuthenticated,
        isAuthError,
        isPending,
        sessionTimeoutMinutes,
        sessionTimeoutWarningMinutes,
        verifiedIdEnabled,
        tapEnabled,
        requiresIdentityReauth,
        verifiedIdIssuance,
    ]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};